import { PGoldPrice, FpPrice } from "../../../common/types";
import { ResponsiveContainer, XAxis, YAxis, Line, LineChart, Tooltip } from "recharts";
import { formatDayTime, calculateTicks, formatCurrency } from "../../helpers/helpers";
import { useState } from "react";
import { PGoldTooltip, secondsDict } from "../utils/ChartUtils";

export function Button({
  text,
  selected,
  setSelected,
  isDarkMode,
}: {
  text: string;
  selected: boolean;
  setSelected: (text: string) => void;
  isDarkMode: boolean;
}) {
  return (
    <button
      className={`rounded-lg border-[1px] px-2 py-1
      ${isDarkMode ? "border-darkgreen" : "border-brown"}
      ${
        selected
          ? isDarkMode
            ? "bg-darkblack text-white"
            : "bg-brown text-white"
          : isDarkMode
            ? "bg-darkblue text-darkgreen"
            : "bg-white text-brown"
      }`}
      onClick={() => setSelected(text)}
    >
      {text}
    </button>
  );
}

export function PGoldChart({
  pGoldPrices,
  fpPrice,
  isPhone,
  isDarkMode,
}: {
  pGoldPrices: PGoldPrice[];
  fpPrice: FpPrice;
  isPhone: boolean;
  isDarkMode: boolean;
}) {
  const [selectedTimeRange, setSelectedTimeRange] = useState("1D");
  const [selectedCurrency, setSelectedCurrency] = useState("DEFAULT");
  const [isUSDToggled, setIsUSDToggled] = useState(false);

  const data = pGoldPrices.filter(
    (price) => price.timestamp > Date.now() / 1000 - secondsDict[selectedTimeRange],
  );

  const getPriceInSelectedCurrency = (priceInFP: number) => {
    if (selectedCurrency === "USD") {
      return (1 / priceInFP) * fpPrice.fp_price;
    }
    if (selectedCurrency === "FP") {
      return 1 / priceInFP;
    }
    return priceInFP;
  };

  return (
    <div
      className={`my-2 ml-4 mr-4 flex w-[80%] flex-col items-start justify-center rounded-lg border-2 p-4 text-sm ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
    >
      <div className="flex w-full justify-between">
        <div className="flex flex-row">
          <div className="mb-4 font-sans text-xl">$pGold</div>
          <div className="mb-4 ml-1 flex flex-row space-x-1 font-sans text-sm">
            <div className="ml-2 mr-2">
              <Button
                text="$pG/$FP"
                selected={selectedCurrency === "DEFAULT"}
                setSelected={() => setSelectedCurrency("DEFAULT")}
                isDarkMode={isDarkMode}
              />
            </div>
            {!isPhone && (
              <>
                <Button
                  text={isUSDToggled ? "$USD/$pG" : "$FP/$pG"}
                  selected={selectedCurrency === "FP" || selectedCurrency === "USD"}
                  setSelected={() => {
                    setSelectedCurrency("FP");
                    setIsUSDToggled(false);
                  }}
                  isDarkMode={isDarkMode}
                />
                {(selectedCurrency === "FP" || selectedCurrency === "USD") && (
                  <div className="flex flex-row items-center">
                    <div
                      className={`relative ml-2 h-6 w-10 cursor-pointer rounded-full border-2
                          ${
                            isDarkMode
                              ? isUSDToggled
                                ? "border-darkgreen bg-darkblue"
                                : "border-darkgreen bg-darkblue"
                              : isUSDToggled
                                ? "border-brown bg-white"
                                : "border-brown bg-white"
                          }`}
                      onClick={() => {
                        const chartToggle = !isUSDToggled;
                        setIsUSDToggled(chartToggle);
                        setSelectedCurrency(chartToggle ? "USD" : "FP");
                      }}
                    >
                      <div
                        className={`m-[.135rem] h-4 w-4 rounded-full ${isDarkMode ? "bg-darkgreen" : "bg-brown"} ${
                          isUSDToggled ? "translate-x-full transform" : ""
                        }`}
                      ></div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="mb-4 space-x-1 font-sans text-sm">
          <Button
            text="1D"
            selected={selectedTimeRange === "1D"}
            setSelected={setSelectedTimeRange}
            isDarkMode={isDarkMode}
          />
          <Button
            text="7D"
            selected={selectedTimeRange === "7D"}
            setSelected={setSelectedTimeRange}
            isDarkMode={isDarkMode}
          />
          <Button
            text="1M"
            selected={selectedTimeRange === "1M"}
            setSelected={setSelectedTimeRange}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>
      <ResponsiveContainer width="100%" className="ml-1" height={300}>
        <LineChart data={data} margin={{ left: 12 }}>
          <XAxis
            dataKey="timestamp"
            type="number"
            tickFormatter={formatDayTime}
            ticks={calculateTicks(data)}
            domain={["dataMin", "dataMax"]}
            stroke={isDarkMode ? "#37b764" : "#342e2e"}
          />
          <YAxis
            dataKey={(price) => getPriceInSelectedCurrency(price.pgold_price)}
            stroke={isDarkMode ? "#37b764" : "#342e2e"}
            domain={["auto", "auto"]}
            tickFormatter={formatCurrency}
          />
          <Line
            type="monotone"
            dataKey={(price) => getPriceInSelectedCurrency(price.pgold_price)}
            stroke={isDarkMode ? "#37b764" : "#342e2e"}
            dot={false}
          />
          <Tooltip
            content={<PGoldTooltip selectedCurrency={selectedCurrency} isDarkMode={isDarkMode} />}
            cursor={{ stroke: isDarkMode ? "#37b764" : "#342e2e", strokeWidth: 1 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
